/**
 * Thao tác cấu trúc trên mảng phẳng XLSForm (`survey`).
 *
 * Question Builder chỉnh sửa trực tiếp mảng dòng chứ không dựng một mô hình
 * trung gian rồi ghi ngược — vì mảng dòng CHÍNH LÀ thứ được lưu vào
 * `form_versions.survey_schema` và xuất ra XLSForm. Mọi thao tác dưới đây vì thế
 * chỉ là cắt/ghép mảng, giữ nguyên mọi cột chưa biết tới (ui_*, parameters…).
 *
 * Mỗi nút mang `start`/`end` là chỉ số dòng: câu hỏi có start === end; nhóm có
 * `start` ở dòng `begin ...` và `end` ở dòng `end ...` khớp cặp.
 */

export const GROUP_TYPES = { group: ['begin group', 'end group'], repeat: ['begin repeat', 'end repeat'] };

/** Cột trống chuẩn của một dòng XLSForm, để dòng mới có đủ cột khi xuất file. */
export function blankRow(extra = {}) {
  return {
    type: '', name: '', label: '', hint: '', required: '', required_message: '',
    constraint: '', constraint_message: '', relevant: '', appearance: '',
    choice_filter: '', calculation: '', repeat_count: '', parameters: '',
    default: '', readonly: '', ...extra,
  };
}

/**
 * Dựng cây từ mảng dòng. Dòng trống (type rỗng) bị bỏ qua ở cây nhưng vẫn nằm
 * trong mảng — chúng là khoảng cách thẩm mỹ của bản gốc, không phải dữ liệu.
 */
export function buildBuilderTree(rows) {
  const root = { kind: 'root', row: null, start: -1, end: rows.length, children: [], parent: null };
  const stack = [root];
  rows.forEach((row, i) => {
    const t = String(row.type || '').trim();
    if (!t) return;
    const parent = stack[stack.length - 1];
    if (t === 'begin group' || t === 'begin repeat') {
      const node = {
        kind: t === 'begin group' ? 'group' : 'repeat',
        row, start: i, end: i, children: [], parent,
      };
      parent.children.push(node);
      stack.push(node);
    } else if (t === 'end group' || t === 'end repeat') {
      const node = stack.pop();
      if (node && node !== root) node.end = i;
    } else {
      parent.children.push({ kind: 'question', row, start: i, end: i, children: [], parent });
    }
  });
  return root;
}

export function walkTree(node, out = []) {
  (node.children || []).forEach((c) => {
    out.push(c);
    walkTree(c, out);
  });
  return out;
}

export function findNode(rows, start) {
  return walkTree(buildBuilderTree(rows)).find((n) => n.start === Number(start)) || null;
}

/** Toàn bộ tên biến đang dùng — để chặn đặt trùng (một `answers` phẳng thì trùng là mất dữ liệu). */
export function usedNames(rows, ignoreStart = null) {
  const out = new Set();
  rows.forEach((r, i) => {
    if (i === ignoreStart) return;
    const n = String(r.name || '').trim();
    if (n) out.add(n);
  });
  return out;
}

/** Đổi chỗ một nút với nút anh/em liền kề. dir = -1 (lên) hoặc +1 (xuống). */
export function moveNode(rows, start, dir) {
  const node = findNode(rows, start);
  if (!node) return rows;
  const siblings = node.parent.children;
  const idx = siblings.indexOf(node);
  const target = siblings[idx + dir];
  if (!target) return rows;

  const block = rows.slice(node.start, node.end + 1);
  const targetBlock = rows.slice(target.start, target.end + 1);
  const next = rows.slice();
  if (dir < 0) {
    next.splice(target.start, targetBlock.length + block.length, ...block, ...targetBlock);
  } else {
    next.splice(node.start, block.length + targetBlock.length, ...targetBlock, ...block);
  }
  return next;
}

/** Xoá một nút và (với nhóm) toàn bộ nội dung bên trong. */
export function deleteNode(rows, start) {
  const node = findNode(rows, start);
  if (!node) return rows;
  const next = rows.slice();
  next.splice(node.start, node.end - node.start + 1);
  return next;
}

/**
 * Chèn dòng mới vào cuối một nhóm (hoặc cuối form nếu `parentStart` là null).
 * Chèn vào *cuối* chứ không phải đầu để thứ tự câu hỏi khớp thứ tự người dùng tạo.
 */
export function insertRows(rows, parentStart, newRows) {
  const next = rows.slice();
  if (parentStart === null || parentStart === undefined) {
    next.push(...newRows);
    return next;
  }
  const node = findNode(rows, parentStart);
  if (!node || node.kind === 'question') {
    next.push(...newRows);
    return next;
  }
  next.splice(node.end, 0, ...newRows);
  return next;
}

/** Thay dòng tại một vị trí bằng bản đã chỉnh (giữ nguyên các cột không đụng tới). */
export function replaceRow(rows, start, patch) {
  const next = rows.slice();
  next[start] = { ...next[start], ...patch };
  return next;
}

/**
 * Đổi tên biến ở mọi nơi tham chiếu tới nó (`${ten}` trong label/hint/relevant/
 * calculation/constraint/repeat_count/choice_filter). Không làm việc này thì đổi
 * tên một câu hỏi sẽ lặng lẽ phá vỡ mọi biểu thức đang trỏ tới nó.
 */
export function renameReferences(rows, oldName, newName) {
  if (!oldName || oldName === newName) return rows;
  const cols = ['label', 'hint', 'relevant', 'calculation', 'constraint', 'repeat_count', 'choice_filter', 'default', 'trigger'];
  const re = new RegExp(`\\$\\{\\s*${oldName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*\\}`, 'g');
  return rows.map((r) => {
    const patch = {};
    cols.forEach((c) => {
      const v = String(r[c] || '');
      if (v && re.test(v)) patch[c] = v.replace(re, `\${${newName}}`);
      re.lastIndex = 0;
    });
    return Object.keys(patch).length ? { ...r, ...patch } : r;
  });
}

/** Danh sách nhóm có thể làm cha khi thêm mới, kèm mức thụt để hiển thị. */
export function groupOptions(rows) {
  const out = [];
  const walk = (node, depth) => {
    node.children.forEach((c) => {
      if (c.kind === 'group' || c.kind === 'repeat') {
        out.push({
          start: c.start,
          depth,
          label: `${'— '.repeat(depth)}${c.row.label || c.row.name || '(chưa đặt tên)'}`,
          kind: c.kind,
        });
        walk(c, depth + 1);
      }
    });
  };
  walk(buildBuilderTree(rows), 0);
  return out;
}
